import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from 'effector-react';
import { Scrollbars } from 'react-custom-scrollbars';
import type { SortEnd } from 'react-sortable-hoc';
import produce from 'immer';
import { nanoid } from 'nanoid';
import { type SceneEffect, SceneType } from '@types';
import Title from '@components/Title';
import Tip from '@components/Tip';
import { CollapseWrapper } from '@components/Collapse';
import Button from '@components/Button';
import { $sceneMapApi } from '@stores/dataStores';
import SortableList from '@components/SortableList';
import arrayMove from '@utils/arrayMove';
import { $loadedScene } from '@stores/loadedStore';
import { showApplyWarning } from '@components/ConfirmModal';
import { sendSuccess } from '@components/Noti';
import SidebarHeader from '../../sidebar/components/SidebarHeader';
import { SidebarEffectItem } from './SidebarEffectItem';
import { PanelEffect } from '../PanelEffect';
import { krpAddEffect, krpRemoveEffect } from '../actions/krpEffects';
import SidebarApplyTo from '../../sidebar/components/SidebarApplyTo';
import { showSceneModal } from '../../category';

const MAX = 5;

export function SidebarEffect() {
  const scene = useStore($loadedScene)!;
  const [editPanel, setEditPanel] = useState<{
    data?: SceneEffect;
    isOn: boolean;
  }>({ isOn: false });

  const list = scene.effect ?? [];
  const listRef = useRef(list);

  useEffect(() => {
    listRef.current = list;
  }, [list]);

  useEffect(() => {
    list.forEach((item) => {
      krpAddEffect(item);
    });
    return () => {
      listRef.current?.forEach((item) => {
        krpRemoveEffect(item.id);
      });
    };
  }, []);

  const handleItemClick = useCallback(
    (data: SceneEffect) => {
      krpRemoveEffect(data.id);
      setEditPanel({ data, isOn: true });
    },
    [setEditPanel]
  );

  const handleItemDelete = useCallback((id: string) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.effect ??= [];
        draft.effect = draft.effect?.filter((item) => item.id !== id) ?? [];
      },
    });
    krpRemoveEffect(id);
  }, []);

  const handleSortEnd = (sort: SortEnd) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.effect ??= [];
        draft.effect = arrayMove({
          array: draft.effect,
          fromIndex: sort.oldIndex,
          toIndex: sort.newIndex,
        });
      },
    });
  };

  const handleEditDone = (data: SceneEffect, origId?: string) => {
    const newEffect = produce(data, (draft) => {
      draft.id = origId || `e_${nanoid()}`;
    });
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.effect ??= [];
        const index = draft.effect?.findIndex((item) => item.id === origId);
        if (index > -1) {
          draft.effect[index] = newEffect;
        } else {
          draft.effect.push(newEffect);
        }
      },
    });
    krpAddEffect(newEffect);
  };

  const handleApplyTo = async () => {
    if (!list.length) {
      const { confirmed } = await showApplyWarning();
      if (!confirmed) return;
    }
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      multiple: true,
      allowedTypes: [SceneType.pano, SceneType.panoVideo],
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!.effect = list;
      });
    });
    sendSuccess('特效设置已应用到所选场景');
  };

  return (
    <CollapseWrapper>
      <div className="absolute inset-0 flex flex-col">
        <SidebarHeader
          title={
            <div className="flex">
              <Title>特效</Title>
              <Tip className="ml-10 self-end text-base">
                {`${list.length}/${MAX}`}
              </Tip>
            </div>
          }
        />
        <Button
          size="lg"
          className="mb-10 w-full"
          disabled={list.length >= MAX}
          onClick={() => setEditPanel({ isOn: true })}
        >
          添加特效
        </Button>
        {list.length > 0 ? (
          <div className="relative flex-1">
            <SortableList
              axis="y"
              lockAxis="y"
              distance={10}
              onSortEnd={handleSortEnd}
            >
              <Scrollbars>
                {list.map((item, i) => (
                  <SidebarEffectItem
                    key={item.id}
                    index={i}
                    data={item}
                    onClick={handleItemClick}
                    onDeleteClick={handleItemDelete}
                  />
                ))}
              </Scrollbars>
            </SortableList>
          </div>
        ) : (
          <div className="flex-1">
            <Tip className="mt-5 text-center text-base">
              {`每个场景最多可添加${MAX}个特效`}
            </Tip>
            <Tip className="mt-5 text-center text-base">
              添加后支持上下拖拽调整特效顺序
            </Tip>
          </div>
        )}
        <SidebarApplyTo
          className="mt-20"
          title="把特效设置应用到..."
          onClick={handleApplyTo}
        />
      </div>
      {editPanel.isOn && (
        <PanelEffect
          data={editPanel.data}
          onConfirm={handleEditDone}
          onDelete={(id) => handleItemDelete(id)}
          onRequestClose={() => setEditPanel({ isOn: false })}
        />
      )}
    </CollapseWrapper>
  );
}
